import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { setSeoPageCache } from "@/lib/page-cache";
import { ComparisonPage } from "@/components/compare/ComparisonPage";
import { COMPARISONS_BY_SLUG } from "@/data/comparisons";

const SITE = "https://heallexa.com";
// Comparison pages are evergreen, not time-stamped articles.
// Published date is approximated from Heallexa's founding year (2026)
// and used to satisfy og:type "article" requirements so Facebook/LinkedIn
// parsers don't reject the markup for missing article metadata.
const PUBLISHED_TIME = "2026-01-01T00:00:00+00:00";

export const Route = createFileRoute("/compare/$slug")({
 loader: async ({ params }) => {
 await setSeoPageCache();
 const data = COMPARISONS_BY_SLUG[params.slug];
 if (!data) throw notFound();
 return data;
 },
 head: ({ loaderData }) => {
 if (!loaderData) return { meta: [{ title: "Compare | Heallexa" }] };
 const url = `${SITE}/compare/${loaderData.slug}`;
 return {
 meta: [
 { title: loaderData.metaTitle },
 { name: "description", content: loaderData.metaDescription },
 { property: "og:title", content: loaderData.metaTitle },
 { property: "og:description", content: loaderData.metaDescription },
 { property: "og:url", content: url },
 // og:type "article" requires article:published_time to be valid.
 // Heallexa comparison pages are evergreen content published in 2026.
 { property: "og:type", content: "article" },
 { property: "article:published_time", content: PUBLISHED_TIME },
 { property: "article:author", content: "https://www.linkedin.com/in/obristbuasa/" },
 { property: "article:section", content: "Healthcare Directory Comparisons" },
 { name: "twitter:title", content: loaderData.metaTitle },
 { name: "twitter:description", content: loaderData.metaDescription },
 ],
 links: [{ rel: "canonical", href: url }],
 scripts: [
 {
 type: "application/ld+json",
 children: JSON.stringify({
 "@context": "https://schema.org",
 "@type": "FAQPage",
 mainEntity: loaderData.faqs.map((f) => ({
 "@type": "Question",
 name: f.q,
 acceptedAnswer: { "@type": "Answer", text: f.a },
 })),
 }),
 },
 {
 type: "application/ld+json",
 children: JSON.stringify({
 "@context": "https://schema.org",
 "@type": "BreadcrumbList",
 itemListElement: [
 { "@type": "ListItem", position: 1, name: "Home", item: SITE },
 { "@type": "ListItem", position: 2, name: "Compare", item: `${SITE}/compare` },
 { "@type": "ListItem", position: 3, name: `Heallexa vs ${loaderData.competitor}`, item: url },
 ],
 }),
 },
 ],
 };
 },
 component: ComparePage,
 notFoundComponent: () => (
 <div className="min-h-screen flex items-center justify-center p-6 text-center">
 <div>
 <h1 className="text-2xl font-semibold">Comparison not found</h1>
 <p className="mt-2 text-muted-foreground">We don't have that comparison page.</p>
 <Link to="/compare" className="mt-4 inline-block text-primary hover:underline">Back to all comparisons</Link>
 </div>
 </div>
 ),
 errorComponent: ({ error }) => (
 <div className="min-h-screen flex items-center justify-center p-6 text-center">
 <div>
 <h1 className="text-2xl font-semibold">Something went wrong</h1>
 <p className="mt-2 text-muted-foreground">{error.message}</p>
 </div>
 </div>
 ),
});

function ComparePage() {
 const data = Route.useLoaderData();
 return <ComparisonPage data={data} />;
}
